<template>
  <div
    class="product-gallery"
    :class="`product-gallery__${columns}-col`"
  >
    <template v-if="product">
      <swiper
        v-if="mobileStyling"
        a11y
        :modules="modules"
        loop
        calculate-height
        :auto-height="autoHeight"
        :pagination="{
          type: 'progressbar',
        }"
        @swiper="onSwiper"
      >
        <swiper-slide
          v-for="(media, index) of product.media"
          :key="media"
        >
          <template v-if="media.height">
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <img :src="getMobileImage(media)">

            <div
              v-if="mobileStyling &&
                product.videos.length &&
                index === getFirstImageIndex()"
              class="video__mobile-controls"
            >
              <button
                class="icon-button--compact"
                type="button"
                @click.prevent="scrollToVideo"
              >
                <span class="icon-button__label">{{ $string('video.play') }}</span>
                <icon-play-circle />
              </button>
            </div>
          </template>

          <video-element
            v-if="media.poster"
            :srcset="media.sources"
            :poster="media.poster"
            loop
            :autoplay="false"
            :shop-the-look="shopTheLook"
          />
        </swiper-slide>
      </swiper>

      <template v-else>
        <template
          v-for="(media) of product.media"
          :key="media"
        >
          <responsive-image
            v-if="media.height"
            :key="media"
            :image="media"
            min-max="800-1000"
            ratio="484:700"
            sizes="50vw"
            block-class="product-gallery"
            @click="openZoom(media.index)"
          />
          <div
            v-if="media.poster"
            class="product-gallery__video-container"
          >
            <video-element
              :srcset="media.sources"
              :poster="media.poster"
              loop
              :autoplay="false"
              :shop-the-look="shopTheLook"
            />
          </div>
        </template>

        <overlay
          key="productImages"
          direction="left"
          namespace="productImages"
          type="modal"
          block-class="zoom-modal"
        >
          <template #close>
            <icon-close />
          </template>

          <template #body>
            <ul class="zoom-modal__nav">
              <li
                v-for="(image, index) of product.media"
                :key="index"
              >
                <a
                  v-if="image.height"
                  class="zoom-modal__nav-link"
                  :class="{'active': index === activeIndex}"
                  @click="handleNavClick(image.index)"
                >
                  <responsive-image
                    class="zoom-modal__nav-thumbnail"
                    :image="image"
                    min-max="68-68"
                    ratio="68:103"
                    sizes="50vw"
                    positioning="relative"
                  />
                </a>
              </li>
            </ul>

            <div class="zoom-modal__images">
              <template v-for="(image, index) of product.media">
                <responsive-image
                  v-if="image.height"
                  :key="index"
                  ref="zoomImage"
                  :image="image"
                  min-max="400-2900"
                  sizes="50vw"
                  positioning="relative"
                />
              </template>
            </div>
          </template>
        </overlay>
      </template>
    </template>

    <template v-else>
      <responsive-image
        min-max="800-1000"
        ratio="484:700"
        sizes="50vw"
        :class="loadingClass"
      />
      <responsive-image
        v-if="!isMobile"
        min-max="800-1000"
        ratio="484:700"
        sizes="50vw"
        :class="loadingClass"
      />
    </template>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Gallery for use on PDP.
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Boolean} shopTheLook - If gallery is displayed in modal, or not.
 *
 */
import { A11y, Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { values as breakpoints } from '~/config/breakpoints'
import classes from '~/config/classes'
import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'
import VideoElement from '~global/video-element/video-element'

import IconClose from '~icons/misc/close.svg'
import IconPlayCircle from '~icons/misc/play-circle.svg'

export default {
  name: 'ProductGallery',

  components: {
    ResponsiveImage,
    Swiper,
    SwiperSlide,
    Overlay,
    VideoElement,
    IconClose,
    IconPlayCircle,
  },

  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    columns: {
      type: Number,
      default: 2,
    },
    shopTheLook: {
      type: Boolean,
      default: false,
    },
  },

  emits: 'swiper-ready',

  setup() {
    return {
      modules: [
        A11y,
        Navigation,
        Pagination,
      ],
    }
  },

  data() {
    return {
      zoomReady: false,
      activeIndex: 0,
      isPinching: false,
      swiper: null,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Compute if screen is mobile or video is in Shop the Look modal.
     * @returns {Boolean}
     */
    mobileStyling() {
      if (this.isMobile || this.shopTheLook) {
        return true
      }

      return false
    },

    /**
     * Compute if autoHeight should be used.
     * @returns {Boolean}
     */
    autoHeight() {
      if (this.shopTheLook) {
        return false
      }

      return true
    },

    /**
     * Compute if Zoom overlay is open.
     * @returns {Boolean}
     */
    zoomOpen() {
      return this.getActiveOverlay() === 'productImages'
    },

    /**
     * Compute loading class.
     * @returns {String}
     */
    loadingClass() {
      return classes.loading
    },
  },

  mounted() {

    /**
     * Used to delay rendering of slider until after navigation buttons exist.
     */
    nextTick().then(() => {
      this.zoomReady = true
    })
  },

  methods: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Open Zoom Overlay.
     * @param {Number} index
     */
    openZoom(index) {
      this.activeIndex = index

      this.toggleOverlay({
        ignoreDismissed: true,
        namespace: 'productImages',
      })

      setTimeout(() => {
        this.zoomNav()
      }, this.settings.throttle)
    },

    /**
     * Navigate zoom overlay.
     */
    zoomNav() {
      const offset = this.$refs.zoomImage[this.activeIndex].$el.offsetTop
      document.querySelector('.zoom-modal__container').scrollTo({
        top: offset,
        left: 0,
        behavior: 'smooth',
      })
    },

    /**
     * Handle zoom navigation click.
     * @param {Number} index
     */
    handleNavClick(index) {
      this.activeIndex = index
      this.zoomNav()
    },

    /**
     * Callback after Swiper is initialized.
     * @param {Object} swiper - Swiper object.
     */
    onSwiper(swiper) {
      this.swiper = swiper
      this.$emit('swiper-ready', swiper)
    },

    /**
     * Transform image source into resized image url.
     * @param {Object} image
     */
    getMobileImage(image) {
      const ratio = 1.5
      const width = breakpoints.m - 1
      const height = Math.round(width * ratio)
      const parts = image.src.split('.')
      const extension = parts[parts.length - 1]

      parts.splice(parts.length - 1, 1)

      let filepath = parts[parts.length - 1]

      parts.splice(parts.length - 1, 1)

      filepath += `_${width}x${height}_crop_center`

      parts.push(filepath)
      parts.push(extension)
      const url = parts.join('.')

      return url
    },

    /**
     * Scroll to video slide if exists.
     */
    scrollToVideo() {
      if (!this.swiper) {
        return
      }

      this.swiper.slideToLoop(this.product.videoIndexes[0])
    },

    /**
     * Getting first image index in the array of media.
     * Used to add play video button on the image.
     */
    getFirstImageIndex() {
      return this.product.media.findIndex(item => Boolean(item.height))
    },
  },
}
</script>

<style lang="scss">
@import './product-gallery';
@import '@/components/zoom-modal';
</style>
